import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';

const COLLAPSED_DAY_EVENT_LIMIT = 6;

document.addEventListener('DOMContentLoaded', function() {
  // Initialize calendar when DOM is ready
  initializeCalendar();
});

document.addEventListener('turbolinks:load', function() {
  // Re-initialize calendar on turbolinks navigation
  initializeCalendar();
});

function initializeCalendar() {
  const calendarEl = document.getElementById('calendar');
  if (!calendarEl) {
    return;
  }

  const eventsPath = calendarEl.getAttribute('data-events-path');
  if (!eventsPath) {
    console.error('Events path not found');
    return;
  }

  // Destroy existing calendar if it exists
  if (calendarEl._calendar) {
    calendarEl._calendar.destroy();
  }

  const calendar = new Calendar(calendarEl, {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    
    // Header configuration (v6 uses headerToolbar)
    //
    // dayGrid rather than timeGrid: nothing on this calendar has a time of day,
    // and a timeGrid view renders 24 empty hour rows underneath a compact
    // all-day strip. listWeek gives a flat agenda for weeks where a day carries
    // a dozen or more jobs.
    headerToolbar: {
      left: 'prev,next,today',
      center: 'title',
      right: 'dayGridMonth,dayGridWeek,dayGridDay,listWeek'
    },

    // First day of week (Monday)
    firstDay: 1,
    locale: 'en-GB',

    // Week numbers configuration
    weekNumbers: true,
    weekText: 'Week ',
    weekNumberFormat: { week: 'numeric' },
    // Custom week number content to ensure "Week" text appears
    weekNumberContent: function(arg) {
      return { html: '<b>Week ' + arg.num + '</b>' };
    },

    // Button text
    buttonText: {
      today: 'today',
      month: 'month',
      week: 'week',
      day: 'day',
      list: 'list'
    },

    // Show the complete schedule by default. Users who prefer a shorter month
    // can opt into FullCalendar's "+N more" overflow with the Collapse control
    // mounted immediately before the view switcher below.
    dayMaxEvents: false,

    // Let busy rows grow the document instead of overflowing the fixed
    // aspect-ratio view harness, which would leave later events unreachable.
    height: 'auto',

    // Event source
    events: eventsPath,

    // Calendar interactions
    selectable: true,
    editable: true,
    droppable: true,

    // Date selection handler
    select: function(info) {
      // Make AJAX request to load new calendar item form
      fetch('/calendar_items/new', {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/javascript'
        }
      })
      .then(response => response.text())
      .then(script => {
        // Execute the returned JavaScript
        eval(script);
        
        // FullCalendar's selection end is exclusive -- selecting a single day
        // yields the next day as `end`. The form wants the last day the item
        // actually covers, so step back one day.
        const startDate = new Date(info.start);
        const endDate = new Date(info.end.getTime() - 24 * 60 * 60 * 1000);

        // Set date range in the form
        const dateRangeInput = document.getElementById('calendar_item_date_range');
        if (dateRangeInput) {
          dateRangeInput.value = formatDate(startDate) + ' - ' + formatDate(endDate);
        }

        // Set hidden fields
        const startHidden = document.querySelector('.start_hidden');
        const endHidden = document.querySelector('.end_hidden');

        if (startHidden) {
          startHidden.value = formatDateForServer(startDate);
        }
        if (endHidden) {
          endHidden.value = formatDateForServer(endDate);
        }
        
        // Initialize date range picker if function exists
        if (typeof date_range_picker === 'function') {
          date_range_picker();
        }
      })
      .catch(error => {
        console.error('Error loading calendar item form:', error);
      });

      // Unselect the date range
      calendar.unselect();
    },

    // Event drop handler (drag and drop)
    eventDrop: function(info) {
      const event = info.event;

      // Determine object type based on update URL
      let objName, text;
      if (event.extendedProps.update_url && event.extendedProps.update_url.includes('/tasks/')) {
        objName = 'task';
        text = 'task';
      } else {
        objName = 'calendar_item';
        text = 'calendar item';
      }
      
      // Every event is whole-day, so always send a bare date built from the
      // browser's local calendar fields. Sending an ISO timestamp here used to
      // move a task back a day: 00:00 Oslo is 22:00 UTC the previous day, and
      // the date column keeps the date part as written.
      const formatDateForServer = (date, isEndDate = false) => {
        if (!date) return null;

        // FullCalendar's all-day end is exclusive; the server stores it inclusive.
        const adjustedDate = isEndDate ? new Date(date.getTime() - 24 * 60 * 60 * 1000) : date;

        const year = adjustedDate.getFullYear();
        const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
        const day = String(adjustedDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Compared against the dragged start date below, so it has to be built the
      // same way. toISOString() here gave the UTC date, which between midnight
      // and 02:00 Oslo is still yesterday -- letting a task be dragged into the
      // past during those hours.
      const todayDate = formatDateForServer(new Date());

      const eventHash = {
        start_date: formatDateForServer(event.start, false),
        end_date: formatDateForServer(event.end, true)
      };

      const eventData = {};
      eventData[objName] = eventHash;

      const startDate = eventData[objName].start_date;
      const eventText = text.charAt(0).toUpperCase() + text.slice(1);
      
      // Check if event is not being moved to past date
      if (startDate >= todayDate) {
        // Show confirmation dialog
        if (window.dataConfirmModal && window.dataConfirmModal.confirm) {
          window.dataConfirmModal.confirm({
            title: 'Update Calendar Task',
            text: `${eventText} start date will be updated to ${startDate}, click confirm to proceed!`,
            commit: 'Confirm',
            cancel: 'Cancel',
            zIindex: 10099,
            modalClass: 'update-modal',
            commitClass: 'btn btn-success', // Make confirm button green
            cancelClass: 'btn btn-secondary',
            onConfirm: function() {
              // Make AJAX request to update event
              fetch(event.extendedProps.update_url, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                },
                body: JSON.stringify(eventData)
              })
              .then(response => {
                if (!response.ok) {
                  throw new Error('Network response was not ok');
                }
                // Refetch events to show the updated position
                if (window.currentCalendar) {
                  window.currentCalendar.refetchEvents();
                }
              })
              .catch(error => {
                console.error('Error updating event:', error);
                info.revert();
              });
            },
            onCancel: function() {
              info.revert();
            }
          });
        } else {
          // Fallback to simple confirm
          if (confirm(`Update ${eventText} start date to ${startDate}?`)) {
            fetch(event.extendedProps.update_url, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
              },
              body: JSON.stringify(eventData)
            })
            .then(response => {
              if (!response.ok) {
                throw new Error('Network response was not ok');
              }
              // Refetch events to show the updated position
              if (window.currentCalendar) {
                window.currentCalendar.refetchEvents();
              }
            })
            .catch(error => {
              console.error('Error updating event:', error);
              info.revert();
            });
          } else {
            info.revert();
          }
        }
      } else {
        info.revert();
        alert('Event cannot be updated to previous date!');
      }
    },

    // Event resize handler
    eventResize: function(info) {
      const event = info.event;
      
      // Determine object type based on update URL
      let objName;
      if (event.extendedProps.update_url && event.extendedProps.update_url.includes('/tasks/')) {
        objName = 'task';
      } else {
        objName = 'calendar_item';
      }
      
      // Every event is whole-day, so always send a bare date built from the
      // browser's local calendar fields. Sending an ISO timestamp here used to
      // move a task back a day: 00:00 Oslo is 22:00 UTC the previous day, and
      // the date column keeps the date part as written.
      const formatDateForServer = (date, isEndDate = false) => {
        if (!date) return null;

        // FullCalendar's all-day end is exclusive; the server stores it inclusive.
        const adjustedDate = isEndDate ? new Date(date.getTime() - 24 * 60 * 60 * 1000) : date;

        const year = adjustedDate.getFullYear();
        const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
        const day = String(adjustedDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const eventHash = {
        start_date: formatDateForServer(event.start, false),
        end_date: formatDateForServer(event.end, true)
      };
      
      const eventData = {};
      eventData[objName] = eventHash;
      
      // Make AJAX request to update event
      fetch(event.extendedProps.update_url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify(eventData)
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        // Refetch events to show the updated size
        if (window.currentCalendar) {
          window.currentCalendar.refetchEvents();
        }
      })
      .catch(error => {
        console.error('Error updating event resize:', error);
        info.revert();
      });
    },

    // Event click handler
    eventClick: function(info) {
      const event = info.event;
      
      if (event.extendedProps.show_url) {
        // Make AJAX request to load event details
        fetch(event.extendedProps.show_url, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/javascript'
          }
        })
        .then(response => response.text())
        .then(script => {
          // Execute the returned JavaScript
          eval(script);
        })
        .catch(error => {
          console.error('Error loading event details:', error);
        });
      }
    },

    // Split the client/order summary, job title and assigned crew into distinct
    // visual elements. The list keeps the crew on the right; day-grid views use
    // the same information in a compact vertical card.
    eventContent: function(arg) {
      const isListView = arg.view.type.indexOf('list') === 0;
      const isDayGridView = arg.view.type.indexOf('dayGrid') === 0;

      if (!isListView && !isDayGridView) {
        return true;
      }

      const props = arg.event.extendedProps || {};
      const inspectors = props.inspectors || [];

      const row = document.createElement('div');
      row.className = 'calendar-event-row calendar-event-row--' +
        (isListView ? 'list' : 'grid');

      const text = document.createElement('span');
      text.className = 'calendar-event-text';

      const summary = document.createElement('span');
      summary.className = 'calendar-event-summary';
      // textContent, not innerHTML: titles and names are user-entered.
      summary.textContent = props.summary || arg.event.title;
      text.appendChild(summary);

      // The job's own name, where the feed sends one. Absent on calendar items,
      // and on the feeds whose summary is already the job name.
      if (props.job_name) {
        const name = document.createElement('span');
        name.className = 'calendar-event-name';
        name.textContent = props.job_name;
        text.appendChild(name);
      }

      row.appendChild(text);

      if (inspectors.length) {
        const crew = document.createElement('span');
        crew.className = 'calendar-event-crew';

        const pills = document.createElement('span');
        pills.className = 'calendar-event-pills';

        inspectors.forEach(function(name) {
          const pill = document.createElement('span');
          pill.className = 'calendar-event-pill';
          pill.textContent = name;
          pills.appendChild(pill);
        });

        crew.appendChild(pills);
        row.appendChild(crew);
      }

      return { domNodes: [row] };
    },

    // Event rendering customization
    eventDidMount: function(info) {
      // Apply custom styles if needed
      const event = info.event;
      
      // Set background color if provided
      if (event.backgroundColor) {
        info.el.style.backgroundColor = event.backgroundColor;
      }
      
      // Set text color if provided
      if (event.textColor) {
        info.el.style.color = event.textColor;
      }
      
      // Set border color if provided
      if (event.borderColor) {
        info.el.style.borderColor = event.borderColor;
      }
    }
  });

  // Store calendar instance for cleanup
  calendarEl._calendar = calendar;
  
  // Make calendar globally accessible for server responses
  window.currentCalendar = calendar;
  
  // Render the calendar
  calendar.render();

  // FullCalendar does not expose a checkbox toolbar item, so mount a small,
  // accessible control immediately before the view switcher. Re-mounting is
  // safe if FullCalendar rebuilds the toolbar after a view or option change.
  mountCalendarCollapseControl(calendarEl, calendar);
}

function mountCalendarCollapseControl(calendarEl, calendar) {
  const toolbarChunk = calendarEl.querySelector(
    '.fc-header-toolbar .fc-toolbar-chunk:last-child'
  );

  if (!toolbarChunk || toolbarChunk.querySelector('.calendar-collapse-control')) {
    return;
  }

  const control = document.createElement('label');
  control.className = 'calendar-collapse-control';

  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.className = 'calendar-collapse-checkbox';
  checkbox.checked = Boolean(calendar.getOption('dayMaxEvents'));
  checkbox.setAttribute('aria-label', 'Collapse busy calendar days');
  control.classList.toggle('is-active', checkbox.checked);

  const text = document.createElement('span');
  text.textContent = 'Collapse';

  control.appendChild(checkbox);
  control.appendChild(text);
  toolbarChunk.prepend(control);

  checkbox.addEventListener('change', function() {
    control.classList.toggle('is-active', checkbox.checked);

    // Rich event cards are taller than FullCalendar's default one-line events.
    // A fixed cap keeps compact mode useful and still leaves enough context to
    // scan the day before opening the remaining-events popover.
    calendar.setOption(
      'dayMaxEvents',
      checkbox.checked ? COLLAPSED_DAY_EVENT_LIMIT : false
    );

    // setOption can rebuild FullCalendar's toolbar. Restore the checkbox on the
    // next frame and derive its state from the calendar option.
    window.requestAnimationFrame(function() {
      mountCalendarCollapseControl(calendarEl, calendar);
    });
  });
}

// Display format for the date-range picker input. Must match the picker's own
// locale format in home/date_range_picker.js, or it cannot parse what we write.
function formatDate(date) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
}

// Wire format for the hidden start_date/end_date fields. Date only -- calendar
// items are whole-day.
function formatDateForServer(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

// External events functionality (if needed)
function initializeExternalEvents() {
  const externalEvents = document.querySelectorAll('#external-events .external-event');
  
  externalEvents.forEach(function(eventEl) {
    const eventObject = {
      title: eventEl.textContent.trim()
    };
    
    // Store the event object in the DOM element
    eventEl.eventObject = eventObject;
    
    // Make it draggable (you might need to implement this with a drag library)
    // This is a placeholder - you'd need to implement drag functionality
    eventEl.draggable = true;
  });
}

// Initialize external events when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  initializeExternalEvents();
});

// Export functions for global access if needed
window.initializeCalendar = initializeCalendar;
window.initializeExternalEvents = initializeExternalEvents;
